import { useState, useContext, useEffect, useCallback, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Menu, Wallet, CreditCard, Users,
  Bell, Settings, LogOut, Home, X, // ShieldCheck,
  Landmark, MessageSquare, Activity, Mail
} from "lucide-react";
import { UserContext } from "../context/UserContext";
import { AuthContext } from "../context/AuthContext";
import api from "../api/axios";
import toast from "react-hot-toast";
import getInitials from "../utils/getInitials";

export default function Layout({ children }) {
  const { user: profile } = useContext(UserContext);
  const { user: authUser, logout } = useContext(AuthContext);

  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [markingNotificationId, setMarkingNotificationId] = useState(null);
  const seenNotificationIdsRef = useRef(new Set());
  const notificationsPrimedRef = useRef(false);

  const pageType = authUser?.role === "admin"
  ? "admin"
  : authUser && !["/login", "/register"].includes(location.pathname)
  ? "user"
  : "public";


  const fetchNotifications = useCallback(async () => {
    if (!authUser) return;
    try {
      setNotificationsLoading(true);
      let data;
      try {
        const unreadRes = await api.get("/notifications/unread");
        data = unreadRes.data;
      } catch (error) {
        // Backward compatibility: if shared endpoint is unavailable, try admin endpoint.
        if (error.response?.status === 404 && authUser?.role === "admin") {
          const fallbackRes = await api.get("/admin/notifications/unread");
          data = fallbackRes.data;
        } else {
          throw error;
        }
      }

      setNotifications(Array.isArray(data) ? data : []);
    } catch (error) {
      // Avoid noisy console spam for unavailable endpoints during transitions.
      if (error.response?.status !== 404) {
        console.error("Failed to load notifications", error);
      }
      setNotifications([]);
    } finally {
      setNotificationsLoading(false);
    }
  }, [authUser]);

  const menuItems =
    pageType === "admin"
      ? [
          { name: "Dashboard", icon: <Home size={20} />, path: "/admin" },
           { name: "Send Money", icon: <Users size={20} />, path: "/send-money" },
          // { name: "Crypto", icon: <Wallet size={20} />, path: "/crypto" },
          { name: "MekHub", icon: <Landmark size={20} />, path: "/mekhub" },
          { name: "Dashboard", icon: <Home size={20} />, path: "/dashboard" },
          { name: "Wallet", icon: <Wallet size={20} />, path: "/wallet" },
          { name: "Fund Wallet", icon: <CreditCard size={20} />, path: "/fund" },
          // { name: "Withdraw", icon: <CreditCard size={20} />, path: "/withdraw" },
          { name: "Pay Bills", icon: <CreditCard size={20} />, path: "/pay-bills" },
          { name: "Users", icon: <Users size={20} />, path: "/admin/users" },
          { name: "Transactions", icon: <CreditCard size={20} />, path: "/admin/transactions" },
          { name: "Transfers", icon: <Landmark size={20} />, path: "/admin/transfers" },
          { name: "Contact Inbox", icon: <MessageSquare size={20} />, path: "/admin/contact-messages" },
          { name: "Newsletter", icon: <Mail size={20} />, path: "/admin/newsletter" },
          { name: "Webhooks", icon: <Activity size={20} />, path: "/admin/webhooks" },
          // { name: "Crypto Approvals", icon: <ShieldCheck size={20} />, path: "/admin/crypto-approvals" },
          { name: "Notifications", icon: <Bell size={20} />, path: "/admin/notifications" },
          { name: "Messages", icon: <MessageSquare size={20} />, path: "/messages" },
          { name: "Settings", icon: <Settings size={20} />, path: "/admin/settings" },
          { name: "KYC", icon: <Settings size={20} />, path: "/admin/kyc" },
        ]
      : pageType === "user"
      ? [
          { name: "Dashboard", icon: <Home size={20} />, path: "/dashboard" },
          { name: "Wallet", icon: <Wallet size={20} />, path: "/wallet" },
          { name: "Fund Wallet", icon: <CreditCard size={20} />, path: "/fund" },
          // { name: "Withdraw", icon: <CreditCard size={20} />, path: "/withdraw" },
          { name: "Pay Bills", icon: <CreditCard size={20} />, path: "/pay-bills" },
          { name: "Settings", icon: <Settings size={20} />, path: "/settings" },
          { name: "KYC", icon: <Settings size={20} />, path: "/kyc" },
          { name: "Transactions", icon: <CreditCard size={20} />, path: "/transactions" },
          { name: "Send Money", icon: <Users size={20} />, path: "/send-money" },
          // { name: "Crypto", icon: <Wallet size={20} />, path: "/crypto" },
          { name: "MekHub", icon: <Landmark size={20} />, path: "/mekhub" },
          { name: "Messages", icon: <MessageSquare size={20} />, path: "/messages" },
        ]
      : [
          { name: "Home", path: "/" },
          { name: "About", path: "/about" },
          { name: "Contact", path: "/contact" },
          { name: "Login", path: "/login" },
          { name: "Register", path: "/register" },
        ];

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (pageType === "user" || pageType === "admin") {
      fetchNotifications();
    }
  }, [pageType, fetchNotifications]);

  useEffect(() => {
    if (!(pageType === "user" || pageType === "admin")) return undefined;
    const intervalId = setInterval(() => {
      if (document.visibilityState === "visible") {
        fetchNotifications();
      }
    }, 60000);
    return () => clearInterval(intervalId);
  }, [pageType, fetchNotifications]);

  useEffect(() => {
    const currentIds = new Set(notifications.map((item) => item._id));

    if (!notificationsPrimedRef.current) {
      seenNotificationIdsRef.current = currentIds;
      notificationsPrimedRef.current = true;
      return;
    }

    const newNotifications = notifications.filter((item) => !seenNotificationIdsRef.current.has(item._id));
    newNotifications.forEach((item) => {
      if (item.type === "chat" || item.type === "support") {
        toast(item.message, {
          icon: "💬",
        });
      }
    });

    seenNotificationIdsRef.current = currentIds;
  }, [notifications]);

  useEffect(() => {
    if (showNotifications && (pageType === "user" || pageType === "admin")) {
      fetchNotifications();
    }
  }, [showNotifications, pageType, fetchNotifications]);

  const handleMarkNotificationAsRead = async (id) => {
    if (markingNotificationId) return;
    setMarkingNotificationId(id);
    try {
      await api.patch(`/notifications/${id}/read`);
      setNotifications((prev) => prev.filter((item) => item._id !== id));
    } catch (error) {
      console.error("Failed to mark notification as read", error);
    } finally {
      setMarkingNotificationId(null);
    }
  };

  // force no horizontal scrollbar globally
  useEffect(() => {
    document.body.style.overflowX = "hidden";
    document.documentElement.style.overflowX = "hidden";
    // Set body background to match page type so scrollbar track never shows white
    document.body.style.backgroundColor = pageType === "public" ? "#ffffff" : "#020617";
    return () => {
      document.body.style.overflowX = "";
      document.documentElement.style.overflowX = "";
      document.body.style.backgroundColor = "";
    };
  }, [pageType]);

  return (
    <div
      className={`min-h-screen w-full font-inter ${
        pageType === "public" ? "bg-white overflow-x-hidden" : "flex bg-slate-950 overflow-x-hidden"
      }`}
    >
      {/* Sidebar (user/admin only) */}
      {(pageType !== "public" || open) && (
        <>
          <div
  className={`bg-slate-900 border-r border-white/10 text-white flex flex-col fixed md:relative h-full z-40
    transform transition-transform duration-300 ease-in-out
    ${open ? "translate-x-0 w-64" : "-translate-x-full md:translate-x-0 md:w-64"}`}
>
  {/* Sidebar Header */}
  <div className="flex items-center justify-between p-4 border-b border-white/10">
    <h1 className="text-xl font-bold">Mek<span className="text-cyan-300">Pay</span></h1>
    <button
      onClick={() => setOpen(false)}
      className="p-2 hover:bg-white/10 rounded-lg md:hidden"
    >
      <X size={20} />
    </button>
  </div>

  {/* Scrollable Menu */}
  <nav className="flex-1 overflow-y-auto px-2 py-6 space-y-2">
    {menuItems.map((item, idx) => {
      const active = location.pathname === item.path;
      return (
        <Link
          key={idx}
          to={item.path}
          className={`flex items-center gap-3 px-3 py-2 rounded-lg transition ${
            active ? "bg-cyan-300/15 text-cyan-300 font-semibold" : "text-slate-300 hover:bg-white/5 hover:text-white"
          }`}
        >
          {item.icon}
          <span>{item.name}</span>
        </Link>
      );
    })}
  </nav>

  {/* Sidebar Footer / Logout */}
  {(pageType === "user" || pageType === "admin") && (
    <div className="p-4 border-t border-white/10 flex-shrink-0">
      <button
        onClick={handleLogout}
        className="flex items-center gap-2 w-full px-3 py-2 hover:bg-white/10 rounded-lg"
      >
        <LogOut size={20} />
        <span>Logout</span>
      </button>
    </div>
  )}
</div>


          {open && (
            <div
              className="fixed inset-0 bg-black/40 z-30 md:hidden"
              onClick={() => setOpen(false)}
            ></div>
          )}
        </>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-x-hidden">
        <header
          className={`p-4 flex justify-between items-center w-full z-50 ${
            pageType === "public"
              ? "fixed top-0 left-0 bg-white/90 backdrop-blur-md border-b border-gray-200 shadow-sm"
              : "sticky top-0 bg-slate-900/95 backdrop-blur-md border-b border-white/10"
          }`}
        >
          <div className="flex items-center gap-2">
            {pageType !== "public" && (
              <button
                onClick={() => setOpen(true)}
                className="p-2 hover:bg-white/10 rounded-md md:hidden text-slate-300"
              >
                <Menu size={20} />
              </button>
            )}
            <h1 className="text-lg font-semibold text-white">
              {pageType === "admin"
                ? "Admin Dashboard"
                : pageType === "user"
                ? "Dashboard"
                : "MekPay"}
            </h1>
          </div>

          {pageType === "user" || pageType === "admin" ? (
            <div className="flex items-center gap-4">
              {/* Notifications */}
              <div className="relative">
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative p-2 hover:bg-white/10 rounded-full text-slate-300"
                >
                  <Bell size={20} />
                  {notifications.length > 0 && (
                    <span className="absolute top-1 right-1 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                      {notifications.length}
                    </span>
                  )}
                </button>
                {showNotifications && (
                  <div className="fixed left-2 right-2 top-[4.5rem] z-50 overflow-hidden rounded-2xl border border-white/15 bg-slate-900 shadow-2xl md:absolute md:right-0 md:left-auto md:top-auto md:mt-2 md:w-[44rem]">
                    <div className="p-3 border-b border-white/10 font-semibold text-white">
                      Notifications
                    </div>
                    <ul className="max-h-[60vh] overflow-y-auto md:max-h-80">
                      {notificationsLoading && (
                        <li className="p-3 text-sm text-slate-400">Loading notifications...</li>
                      )}
                      {!notificationsLoading && notifications.length === 0 && (
                        <li className="p-3 text-sm text-slate-400">No new notifications.</li>
                      )}
                      {!notificationsLoading && notifications.map((n) => (
                        <li
                          key={n._id}
                          className="p-4 text-sm border-b border-white/10 last:border-none hover:bg-white/5 text-slate-200"
                        >
                          <p className="mb-2 whitespace-normal break-words leading-relaxed">{n.message}</p>
                          <div className="flex items-start justify-between gap-3">
                            <span className="text-xs uppercase text-slate-500 flex-shrink-0">{n.type}</span>
                            <button
                              type="button"
                              onClick={() => handleMarkNotificationAsRead(n._id)}
                              disabled={Boolean(markingNotificationId)}
                              aria-busy={markingNotificationId === n._id}
                              className="text-xs text-cyan-300 hover:underline whitespace-nowrap"
                            >
                              {markingNotificationId === n._id ? "Marking…" : "Mark read"}
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2">
                <div
                  aria-label={`${profile?.name || "User"} initials`}
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-cyan-300/25 bg-cyan-300/10 text-[11px] font-semibold text-cyan-200"
                >
                  {getInitials(profile?.name)}
                </div>
                <span className="hidden md:block font-medium text-white">
                  {profile?.name || "John Doe"}
                </span>
              </div>
            </div>
          ) : (
            <nav className="space-x-4 hidden md:flex">
              {menuItems.map((item, idx) => (
                <Link
                  key={idx}
                  to={item.path}
                  className="hover:text-primary font-medium"
                >
                  {item.name}
                </Link>
              ))}
            </nav>
          )}
        </header>

        <main
          className={`flex-1 min-w-0 w-full ${
            pageType === "public"
              ? "pt-[5rem] bg-white overflow-x-hidden" // push content below fixed header
              : "p-6 bg-slate-950 overflow-x-hidden"
          }`}
        >
          {children}
        </main>
      </div>
    </div>
  );
}
